/**
 * Tracking values, computed the way the API computes them (`campaigns/codes.py`),
 * so what the create flow previews is what saving produces: the same slug, the
 * same UTM set, the same URL shape, the same promo-code format. Pure functions.
 */

import { defaultUtm, type ChannelKind, type ChannelType, type TrackingDraft } from '@/api/campaigns'

/** Lowercase, without characters people misread off a printed flyer. */
export const SHORT_CODE_ALPHABET = '23456789abcdefghjkmnpqrstuvwxyz'
export const SHORT_CODE_LENGTH = 7
const PROMO_SUFFIX_ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ'
const PROMO_SUFFIX_LENGTH = 4
const PROMO_PREFIX_MAX = 10
const SLUG_MAX = 80

export const UTM_VALUE_PATTERN = /^[A-Za-z0-9._-]{1,100}$/
/** 3 to 32 letters, digits and single inner hyphens; matched case-insensitively. */
export const PROMO_CODE_PATTERN = /^[A-Z0-9](?:[A-Z0-9]|-(?=[A-Z0-9])){2,31}$/

export function slugify(value: string, fallback = 'campaign'): string {
  const ascii = value.normalize('NFKD').replace(/[^\x20-\x7e]/g, '')
  let slug = ascii
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  if (slug.length > SLUG_MAX) slug = slug.slice(0, SLUG_MAX).replace(/-+$/, '')
  return slug || fallback
}

/** Why `path` cannot be a landing path, or null. Empty means the site root. */
export function landingPathError(path: string): string | null {
  if (path === '') return null
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\') || [...path].some((char) => char.charCodeAt(0) < 0x20)) {
    return 'A landing path is a path on your website, starting with a single “/”.'
  }
  if (/^\/[^/?#]*:/.test(path)) return 'A landing path must not name a scheme or a host.'
  return null
}

export function utmValueError(value: string, label: string): string | null {
  return UTM_VALUE_PATTERN.test(value) ? null : `${label} may use letters, digits, “.”, “_” and “-” only.`
}

export function normalisePromoCode(raw: string): string | null {
  const candidate = raw.trim().toUpperCase()
  return PROMO_CODE_PATTERN.test(candidate) ? candidate : null
}

export function promoPrefix(campaignSlug: string): string {
  return campaignSlug.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, PROMO_PREFIX_MAX) || 'PROMO'
}

export interface TrackingUrlParts {
  utmSource: string
  utmMedium: string
  utmCampaign: string
  utmContent: string | null
  /** The saved record's id, sent as `utm_id`; absent in a preview. */
  trackingId: string | null
}

/**
 * The website's URL for the landing path with the UTM set applied. Keys the
 * path already carries are kept; UTM keys it names are replaced, not doubled.
 */
export function buildTrackingUrl(origin: string, landingPath: string | null, parts: TrackingUrlParts): string {
  const base = new URL(origin)
  const target = new URL(landingPath && !landingPathError(landingPath) ? landingPath : '/', 'http://placeholder')
  const utm: Record<string, string> = {
    utm_source: parts.utmSource,
    utm_medium: parts.utmMedium,
    utm_campaign: parts.utmCampaign,
  }
  if (parts.trackingId) utm.utm_id = parts.trackingId
  if (parts.utmContent) utm.utm_content = parts.utmContent
  const query = new URLSearchParams([...target.searchParams].filter(([key]) => !(key in utm)))
  for (const key of Object.keys(utm).sort()) query.append(key, utm[key] as string)
  return `${base.origin}${target.pathname}?${query.toString()}${target.hash}`
}

function fnv(value: string): number {
  let hash = 0x811c9dc5
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

/** A repeatable code from `parts`: the same inputs always propose the same code. */
export function deterministicCode(parts: readonly string[], alphabet: string, length: number): string {
  let state = fnv(parts.join(':'))
  let code = ''
  for (let index = 0; index < length; index += 1) {
    state = Math.imul(state ^ (state >>> 15), 0x2c1b3c6d) >>> 0
    state = (state ^ fnv(`${index}:${state}`)) >>> 0
    code += alphabet[state % alphabet.length]
  }
  return code
}

export function shortCodeFor(...parts: string[]): string {
  return deterministicCode(parts, SHORT_CODE_ALPHABET, SHORT_CODE_LENGTH)
}

/** `SUMMERDRAW-7K3Q`: the campaign, then a suffix. */
export function promoCodeFor(campaignSlug: string, ...parts: string[]): string {
  return `${promoPrefix(campaignSlug)}-${deterministicCode(parts, PROMO_SUFFIX_ALPHABET, PROMO_SUFFIX_LENGTH)}`
}

/** The tracking set-up a newly chosen channel starts with. Offline channels get a QR code and a promo code. */
export function defaultTrackingDraft(type: ChannelType, kind: ChannelKind, name: string): TrackingDraft {
  const utm = defaultUtm(type, kind)
  const offline = kind === 'offline'
  return {
    landingPath: '',
    utmSource: utm.source,
    utmMedium: utm.medium,
    utmContent: slugify(name, 'channel'),
    shortLink: true,
    qrCode: offline,
    promoMode: offline ? 'generate' : 'none',
    promoCode: '',
  }
}
